import React from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import {
  HiOutlineChartBarSquare,
  HiOutlineUsers,
  HiOutlineCreditCard,
  HiOutlineBolt,
  HiOutlinePuzzlePiece,
  HiOutlineLifebuoy,
  HiOutlineDocumentText,
  HiOutlineDocumentDuplicate,
  HiOutlineCog6Tooth,
  HiOutlineSparkles,
  HiOutlineChatBubbleLeftEllipsis,
  HiOutlineInbox,
} from "react-icons/hi2";
import { BrandLogo } from "../brand-logo";
import { appConfig } from "../../app-config";
import { APP_ROUTES } from "../../data-access";
import { openSupportChat } from "../../feature/live-chat";
import { useMobileMenu, useSupportUnread, useUser } from "../../hooks";
import { canUseAutoBid } from "../../util/plan-gates";
import { SidebarUserPanel } from "./sidebar-user-panel";
import { SidebarSetupIndicator } from "./sidebar-setup-indicator";
import "./sidebar.scss";

export const SidebarMenu = (): JSX.Element => {
  const { hasAdminRights, currentUser } = useUser();
  const { isMenuOpened, toggleBurgerMenu } = useMobileMenu();

  const isStaff = currentUser?.is_staff === true;
  // Conversations, not messages: the badge counts people waiting, which is what
  // the agent picks off the list one at a time.
  const { conversations: waitingConversations } = useSupportUnread(isStaff);

  const checkedRelease = useSelector(
    (state: { generic: { [key: string]: string } }) =>
      state.generic.checkedRelease
  );
  const isNewReleaseSeen = checkedRelease !== appConfig.version;

  const handleLinkClick = (): void => {
    if (isMenuOpened) {
      toggleBurgerMenu();
    }
  };

  const handleShareFeedback = (): void => {
    handleLinkClick();
    openSupportChat();
  };

  return (
    <>
      <nav className="sidebar">
        <BrandLogo allowNavigation={false} />
        <h1 className="title">Account</h1>
        <NavLink to={APP_ROUTES.dashboard} className={({isActive}) => `nav-item${isActive ? ' active' : ''}`} onClick={handleLinkClick}>
          <HiOutlineChartBarSquare className="icon" />
          Dashboard
        </NavLink>
        <NavLink to={APP_ROUTES.team} className={({isActive}) => `nav-item${isActive ? ' active' : ''}`} onClick={handleLinkClick}>
          <HiOutlineUsers className="icon" />
          Team
        </NavLink>
        {hasAdminRights && (
          <NavLink
            to={APP_ROUTES.billing}
            className={({isActive}) => `nav-item${isActive ? ' active' : ''}`}
            state={{ allowedTransition: true }}
            onClick={handleLinkClick}
          >
            <HiOutlineCreditCard className="icon" />
            Billing
          </NavLink>
        )}
        <NavLink to={APP_ROUTES.aicredits} className={({isActive}) => `nav-item${isActive ? ' active' : ''}`} onClick={handleLinkClick}>
          <HiOutlineBolt className="icon" />
          Credits
        </NavLink>

        <h1 className="title">Bidding</h1>
        <NavLink to={APP_ROUTES.aiTemplates} className={({isActive}) => `nav-item${isActive ? ' active' : ''}`} onClick={handleLinkClick}>
          <HiOutlineDocumentDuplicate className="icon" />
          Cover Letters
        </NavLink>
        {canUseAutoBid(currentUser?.plan) && (
          <NavLink to={APP_ROUTES.autoBidding} className={({isActive}) => `nav-item${isActive ? ' active' : ''}`} onClick={handleLinkClick}>
            <HiOutlineSparkles className="icon" />
            Auto-Bidding
          </NavLink>
        )}
        <NavLink to={APP_ROUTES.aiSettings} className={({isActive}) => `nav-item${isActive ? ' active' : ''}`} onClick={handleLinkClick}>
          <HiOutlineCog6Tooth className="icon" />
          Settings
        </NavLink>

        {isStaff && (
          /* Support inbox. Hiding the link is convenience for the team, not
             the boundary - the API is gated by the `staff` middleware. */
          <NavLink to={APP_ROUTES.support} className={({isActive}) => `nav-item${isActive ? ' active' : ''}`} onClick={handleLinkClick}>
            <HiOutlineInbox className="icon" />
            Support Inbox
            {waitingConversations > 0 && (
              <span
                className="nav-badge"
                title={`${waitingConversations} ${
                  waitingConversations === 1 ? "conversation is" : "conversations are"
                } waiting for a reply`}
              >
                {waitingConversations > 9 ? "9+" : waitingConversations}
              </span>
            )}
          </NavLink>
        )}

        <h1 className="title">More</h1>
        <NavLink to={APP_ROUTES.toolsAndApps} className={({isActive}) => `nav-item${isActive ? ' active' : ''}`} onClick={handleLinkClick}>
          <HiOutlinePuzzlePiece className="icon" />
          Tools & Apps
        </NavLink>
        <NavLink
          to="https://help.upwex.io/"
          className={({isActive}) => `nav-item${isActive ? ' active' : ''}`}
          target="_blank"
          onClick={handleLinkClick}
        >
          <HiOutlineLifebuoy className="icon" />
          Help Center
        </NavLink>
        <NavLink to={APP_ROUTES.releaseNotes} className={({isActive}) => `nav-item${isActive ? ' active' : ''}`} onClick={handleLinkClick}>
          <HiOutlineDocumentText className="icon" />
          Release Notes
          {isNewReleaseSeen && <span className="new-release-indicator" />}
        </NavLink>
        <a className="nav-item" onClick={handleShareFeedback}>
          <HiOutlineChatBubbleLeftEllipsis className="icon" />
          Share Feedback
        </a>
        <div className="sidebar__bottom">
          <SidebarSetupIndicator />
          <SidebarUserPanel />
        </div>
      </nav>

    </>
  );
};

export default SidebarMenu;
